import { useEffect, useState, useMemo } from "react";
import useHomeController from "../../controllers/Home.controllers";
import ReactGA from "react-ga4";
import services from "API/services";
import moment from "moment";
import { ROOM_CATEGORYES, ROOM_CATEGORY } from "constant/constants";
import { daysDiffernce } from "utils/utils";
import { Link } from "react-router-dom";
import TableSkeleton from "components/skeletons/TableSkeleton";
import { GoArrowUpRight } from "react-icons/go";
import { FiArrowDownRight } from "react-icons/fi";
import { TbStarFilled } from "react-icons/tb";
import { Chart } from "react-google-charts";
import SkeletonDashboard from "./SkeletonDashboard";

const MealPlanText = ["EP", "CP", "AP", "MAP"];
const tabOccupancyFilter = ["Today", "Yesterday", "MTD", "YTD",];
const paymentModesForPayAtHotel = [0, 2, 3, 4, 5, 6, 7];
const allStatuses = ["Upcoming", "In-House", "For-Checkout", "Checked-Out", "No-Show", "Cancelled", "Completed"];
const colors = ["#4caf50", "#2196f3", "#ff9800", "#e91e63", "#9e9e9e", "#f44336", "#3f51b5"];
const sourceLabels = {
  rroomsCount: "RROOMS",
  otaCount: "OTA",
  taCount: "TA",
  walkinCount: "Walk-In",
  totalBookings: "Total",
};

const foodStatusLabels = {
  inKitchen: "In-Kitchen",
  readyToServe: "To Serve",
  delivered: "Delivered",
  rejected: "Rejected",
  complementary: "Complementary",
  ncCount: "NC KOT",
  newOrder: "New Orders",
  totalOrders: "All",
};

const capitalizeWords = (str) => {
  if (typeof str !== "string") return "";
  return str.toLowerCase().split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const Landing = () => {

  const [roomsData, setRoomsData] = useState([]);
  const [occupancyData, setOccupancyData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [bookings, setBookings] = useState([])
  const [availabilityTab, setAvailabilityTab] = useState('Today');
  const [occupancyTab, setOccupancyTab] = useState('Today');
  const [currentPage, setCurrentPage] = useState(1)
  const [ratingData, setRatingData] = useState([]);
  const [bookingBySource, setBookingBySource] = useState({})
  const [foodInfo, setFoodInfo] = useState({})
  const [selectedBookingType, setSelectedBookingType] = useState("");

  const { booking, setBookingDates, activeBtn, tabBookingFilter, bookingOption, userDetail, foodStatus, Chart } = useHomeController();

  useEffect(() => { ReactGA.send({ hitType: "pageview", page: "dashboard" }); }, []);

  const getReview = async (id) => {
    try {
      const res = await services.globalService.getCustomerReviewPropertyIdService(id);
      if (res.status == 200) { setRatingData(res.data.data); }
    } catch (error) { console.log('Error Found') }
  };

  useEffect(() => { getReview(userDetail?.propertyId); }, [])

  const getRoomCategory = async () => {
    try {
      const res = await services.property.getRoomCategoryService();
      if (res.status == 200) { setCategoryData(res?.data?.data); }
    } catch (error) { console.log('Error Found') }
  };

  useEffect(() => { getRoomCategory(); }, []);

  const fetchRoomOccupancy = async (propertyId, tabType) => {
    let selected = tabType == 'availability' ? availabilityTab : occupancyTab;
    if (selected == 'Today') {
      const res = await services?.property.roomOccupancyForTodayService(propertyId);
      if (res.data.status) {
        tabType == 'availability' ? setRoomsData(res.data.data) : setOccupancyData(res.data.data);
      }
    }
    else {
      let start, end, url = '';
      if (selected == 'Yesterday') {
        const date = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `reportDate=${date}`;
      }
      else if (selected == 'MTD') {
        start = moment().subtract(30, 'days').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `&startDate=${start}&endDate=${end}`;
      }
      else if (selected == 'YTD') {
        start = moment().subtract(1, 'years').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `&startDate=${start}&endDate=${end}`;
      }
      const res = await services?.property.roomOccupancyByDateService(propertyId, url);
      if (res.data.status) {
        tabType == 'availability' ? setRoomsData(res.data.data) : setOccupancyData(res.data.data);
      }
    }
  };

  useEffect(() => { fetchRoomOccupancy(userDetail?.propertyId, 'availability'); }, [availabilityTab]);

  useEffect(() => { fetchRoomOccupancy(userDetail?.propertyId, 'occupancy'); }, [occupancyTab]);

  const getBooking = async (id = null) => {
    try {
      const query = `?page=${1}&limit=${10}${`&bookingStatus=${1}`}${selectedBookingType ? `&bookingType=${selectedBookingType}` : ""}`;
      const res = await services.globalService.getBookingPropertyIdService(userDetail?.propertyId, query);
      if (res.data.status == true) { { userDetail?.logintype == "propertyUser" && setBookings(res?.data?.data) } }
    }
    catch { console.log('Error Found') }
  }

  useEffect(() => { getBooking() }, [])
  useEffect(() => { getBooking() }, [selectedBookingType])

  const fetchBookingsByArea = async () => {
    let params = `?page=${currentPage}&limit=10`
    const res = await services.property.getBookingsByArea(params)
    if (res.status == 200 && res.data.status == true) { { userDetail?.logintype == "rroomUser" && setBookings(res?.data?.data) } }
  }

  useEffect(() => { if (userDetail?.logintype == "rroomUser") { fetchBookingsByArea() } }, [currentPage])

  // Rating Data
  const validReviews = ratingData?.filter(r => typeof r.rating === 'number');
  const overallRating = (validReviews?.reduce((acc, curr) => acc + curr.rating, 0) / validReviews.length).toFixed(1);

  // Room Counts
  const roomsCount = useMemo(() => {
    return occupancyData?.reduce(
      (acc, curr) => ({
        available: acc.available + (curr?.availableCount || 0),
        blocked: acc.blocked + (curr?.blockedCount || 0),
        occupied: acc.occupied + (curr?.occupiedCount || 0),
        dirty: acc.dirty + (curr?.dirtyCount || 0),
        total:
          acc.total +
          ((curr?.blockedCount || 0) +
            (curr?.occupiedCount || 0) +
            (curr?.dirtyCount || 0) +
            (curr?.availableCount || 0)),
      }),
      { available: 0, blocked: 0, occupied: 0, dirty: 0, total: 0 }
    );
  }, [occupancyData]);


  let totalAvailRoom = 0
  for (let key in roomsCount) { totalAvailRoom += roomsCount[key] }

  // Booking Count
  const filteredBookings = booking.filter((item) => {
    return (
      (bookingOption.title === "All" || item[bookingOption.count] > 0) &&
      item.bookingStatus !== "Pending" &&
      item.bookingStatus !== "Call Not Picked"
    );
  });

  const bookingCounts = filteredBookings.reduce((acc, item) => {
    let bookingName =
      item.bookingStatus === "Confirmed"
        ? "Upcoming"
        : item.bookingStatus === "Checked-In"
          ? "In-House"
          : item.bookingStatus === "Checked-Out"
            ? "Checked-Out"
            : item.bookingStatus === "No-Show"
              ? "No-Show"
              : item.bookingStatus;

    acc[bookingName] = (acc[bookingName] || 0) + (item[bookingOption.count] || 0);
    return acc;
  }, {});

  const allBookingDetails = allStatuses.reduce((acc, status) => {
    acc[status] = bookingCounts[status] || 0;
    return acc;
  }, {});

  // Room Occupancy
  const categoryIdToNameMap = categoryData.reduce((acc, curr) => {
    acc[curr.id] = curr.name;
    return acc;
  }, {});

  const groupedByCategory = {};

  occupancyData?.forEach(item => {
    const catId = item?.categoryId;
    const catName = categoryIdToNameMap[catId] || 'Unknown';
    if (!groupedByCategory[catId]) {
      groupedByCategory[catId] = {
        categoryName: catName,
        CatTotalRooms: 0,
        occupiedCount: 0,
        availableCount: 0,
        dirtyCount: 0,
        blockedCount: 0
      };
    }
    groupedByCategory[catId].CatTotalRooms += Number(item?.CatTotalRooms || item?.catTotalRooms) || 0;
    groupedByCategory[catId].occupiedCount += Number(item?.occupiedCount) || 0;
    // groupedByCategory[catId].availableCount += Number((item?.availableCount) + item?.dirtyCount) || 0;
    groupedByCategory[catId].availableCount += Number(item?.availableCount) || 0;
    groupedByCategory[catId].dirtyCount += Number(item?.dirtyCount) || 0;
    groupedByCategory[catId].blockedCount += Number(item?.blockedCount) || 0;
  });

  const finalCategoryWiseData = Object.values(groupedByCategory);

  const roomOccupancy = [
    ['Room Category', 'Occupancy'],
    ...finalCategoryWiseData.map(item => {
      const percentage = item.CatTotalRooms > 0 ? Math.round((item.occupiedCount * 100) / item.CatTotalRooms) : 0;
      return [`${capitalizeWords(item.categoryName)} - ${percentage}%`, percentage];
    })
  ];

  const hasData =
    roomOccupancy &&
    roomOccupancy.length > 1 &&
    roomOccupancy.some((row, i) => i !== 0 && row[1] > 0);

  const fallbackData = [
    ["Status", "Count"],
    ["No Data", 1],
  ];

  const { totalOccupied, totalRooms } = finalCategoryWiseData.reduce(
    (acc, curr) => {
      acc.totalOccupied += curr.occupiedCount;
      acc.totalRooms += curr.CatTotalRooms;
      return acc;
    },
    { totalOccupied: 0, totalRooms: 0 }
  );

  const occupancyInPercentage = Math.round((totalOccupied * 100) / totalRooms);

  const options = {
    pieHole: 0.4,
    colors: ["#8D33FF", "#FFC300", "#20BF6B"],
    legend: { position: "bottom" },
    chartArea: { width: "90%", height: "74%" },
    tooltip: { trigger: "focus" },
    pieStartAngle: 80,
    slices: { 1: { offset: 0.02 } },
    sliceVisibilityThreshold: 0,
    pieSliceTextStyle: { color: "#000", fontSize: 10, bold: true },
  };

  const getSafePercentage = (value) => {
    if (value == null || isNaN(value)) return 0;
    return Math.min(100, Math.max(0, Number(value)));
  };

  const rawPercentage = roomOccupancy?.[1]?.[1]; // jo bhi API / state se aa raha
  const singleCategoryPercentage = getSafePercentage(rawPercentage);

  const radius = 110;
  const strokeWidth = 14;
  const circumference = 2 * Math.PI * radius;

  const filled = ((100 - singleCategoryPercentage) / 100) * circumference;
  const gap = (singleCategoryPercentage / 100) * circumference;


  const DonutChart = ({ percentage }) => {
    const safePercentage = Math.min(100, Math.max(0, percentage));

    const radius = 110;
    const strokeWidth = 35;
    const circumference = 2 * Math.PI * radius;

    const filled = ((100 - safePercentage) / 100) * circumference;
    const gap = (safePercentage / 100) * circumference;

    return (
      <div style={{ position: "relative", width: 300, height: 300 }}>
        <svg width="330" height="300" viewBox="0 0 300 300">
          {/* Background */}
          <circle
            cx="150"
            cy="150"
            r={radius}
            fill="none"
            stroke="#F3E8FF"
            strokeWidth={strokeWidth}
          />

          {/* Filled */}
          <circle
            cx="150"
            cy="150"
            r={radius}
            fill="none"
            stroke="#009242"
            strokeWidth={strokeWidth}
            strokeDasharray={`${filled} ${circumference}`}
            strokeLinecap="round"
            transform="rotate(-90 150 150)"
          />

          {/* Gap */}
          <circle
            cx="150"
            cy="150"
            r={radius}
            fill="none"
            stroke="#dc3545"
            strokeWidth={strokeWidth}
            strokeDasharray={`${gap} ${circumference}`}
            strokeDashoffset={-filled}
            strokeLinecap="round"
            transform="rotate(-90 150 150)"
          />
        </svg>

        {/* Center Text */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "55%",
            transform: "translate(-50%, -50%)",
            fontSize: "21px",
            fontWeight: 700,
          }}
        >
          {safePercentage}%
        </div>
      </div>
    );
  };

  // Booking Count By Source
  const fetchBookingCountBySource = async () => {
    try {
      let start, end, url = '';
      if (occupancyTab === 'Today') {
        const date = moment().format('YYYY-MM-DD');
        url = `fromDate=${date}&toDate=${date}`;
      }
      if (occupancyTab === 'Yesterday') {
        const date = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `fromDate=${date}&toDate=${date}`;
        setBookingDates(date, date, 'Yesterday')
      }
      else if (occupancyTab === 'MTD') {
        start = moment().subtract(30, 'days').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `fromDate=${start}&toDate=${end}`;
        setBookingDates(start, end, 'MTD')
      }
      else if (occupancyTab === 'YTD') {
        start = moment().subtract(1, 'years').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `fromDate=${start}&toDate=${end}`;
        setBookingDates(start, end, 'YTD')
      }
      const res = await services.property.bookingCountBySourceService(userDetail.propertyId, url)
      if (res?.data?.status == true) { setBookingBySource(res?.data?.data) }
    }
    catch { console.log('Error Found') }
  }

  useEffect(() => { fetchBookingCountBySource() }, [userDetail, occupancyTab])

  // Food Counts By Status
  const fetchFoodCountByStatus = async () => {
    try {
      let start, end, url = '';
      if (occupancyTab === 'Today') {
        const date = moment().format('YYYY-MM-DD');
        url = `fromDate=${date}&toDate=${date}`;
      }
      if (occupancyTab === 'Yesterday') {
        const date = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `fromDate=${date}&toDate=${date}`;
        setBookingDates(date, date, 'Yesterday')

      }
      else if (occupancyTab === 'MTD') {
        start = moment().subtract(30, 'days').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `fromDate=${start}&toDate=${end}`;
        setBookingDates(start, end, 'MTD')
      }
      else if (occupancyTab === 'YTD') {
        start = moment().subtract(1, 'years').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').format('YYYY-MM-DD');
        url = `fromDate=${start}&toDate=${end}`;
        setBookingDates(start, end, 'YTD')
      }
      const res = await services.property.foodsCountByStatusService(userDetail.propertyId, url)

      if (res?.data?.status == true) { setFoodInfo(res?.data?.data) }
    }
    catch { console.log('Error in fetchFoodCountByStatus function') }
  }

  useEffect(() => { fetchFoodCountByStatus() }, [userDetail, occupancyTab])

  const CircleIcon = ({ color }) => (
    <svg width="10" height="10" viewBox="0 0 10 10" style={{ marginRight: "4px", flexShrink: 0 }}>
      <circle cx="5" cy="5" r="5" fill={color} />
    </svg>
  );

  const foodOrder = [
    "newOrder",
    "inKitchen",
    "readyToServe",
    "delivered",
    "rejected",
    "ncCount",
    "complementary",
    "totalOrders"
  ];

  const containerStyle = { display: "flex", flexWrap: "wrap" };
  const boxStyle = { width: "calc(33.33% - 10px)" };
  const halfBoxStyle = { width: "calc(50% - 10px)" };

  return (
    <>
      {
        categoryData?.length > 0 ? (
          <>
            <div className="home-tab">
              <div className="timeTabDashboard tab-nav">
                {tabOccupancyFilter.map((item, ind) => {
                  return (
                    <button key={ind} className={occupancyTab === item && "active"} onClick={() => { setOccupancyTab(item); }}>{item}</button>
                  );
                })}
              </div>
            </div>
            <div className="dash-tiles">
              <div className="row">
                <div className="col-lg-2 col-md-4 col-sm-6 col-6">
                  <div className="column one border border-success">
                    <h4>Available Rooms</h4>
                    <span className="text-success fs-3 fw-bold">{roomsCount?.available}</span>
                    <h5 className="text-success">Total {roomsCount?.total}</h5>
                  </div>
                </div>
                <div className="col-lg-2 col-md-4 col-sm-6 col-6">
                  <div className="column two border border-primary">
                    <h4>{occupancyTab}'s Check-ins</h4>
                    <span className="text-primary fs-3 fw-bold">{bookingCounts['In-House'] || 0}</span>
                    <h5 className="text-primary">{bookingCounts?.Upcoming} Expected</h5>
                  </div>
                </div>
                <div className="col-lg-2 col-md-4 col-sm-6 col-6">
                  <div className="column three">
                    <h4>{occupancyTab}'s Check-outs</h4>
                    <span className="fs-3 fw-bold">{bookingCounts['Checked-Out'] || 0}</span>
                    <h5>For Checkout - {bookingCounts['For-Checkout'] || 0}</h5>
                  </div>
                </div>
                <div className="col-lg-2 col-md-4 col-sm-6 col-6">
                  <div className="column four">
                    <h4>Room Occupancy</h4>
                    <span className="fs-3 fw-bold">{occupancyInPercentage || 0}%</span>
                    <h5>vs 100% Target</h5>
                  </div>
                </div>
                <div className={`col-lg-2 col-md-4 col-sm-6 col-6 ${userDetail?.logintype == "propertyUser" ? 'col-lg-2' : 'col-lg-4 room-overview'}`}>
                  <div className="column five border border-info">
                    <h4 className="mb-0">Room Overview</h4>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div style={{ display: "flex", alignItems: "baseline" }}>
                        <CircleIcon color="green" />
                        <span style={{ color: "black", fontSize: "10px", lineHeight: "10px" }}>Available</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "baseline" }}>
                        <CircleIcon color="blue" />
                        <span style={{ color: "black", fontSize: "10px", lineHeight: "10px" }}>Total</span>
                      </div>
                    </div>
                    {
                      finalCategoryWiseData?.map((item) => {
                        return (
                          <p>
                            <strong className="one" style={{ fontSize: '11px' }}>{item?.categoryName?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())} -</strong>
                            <strong className="two" style={{ color: "green", fontSize: '11px' }}>{item?.availableCount}</strong> /&nbsp;
                            <strong className="three" style={{ color: "blue", fontSize: '11px' }}>{item?.CatTotalRooms}</strong>
                          </p>
                        )
                      })
                    }
                  </div>
                </div>
                {
                  userDetail?.logintype == "propertyUser" &&
                  <div className="col-lg-2 col-md-4 col-sm-6 col-6">
                    <div className="column six border border-danger">
                      <h4>Rating</h4>
                      <span className="text-danger fs-3 fw-bold">
                        {ratingData.length > 0 ? overallRating : 0}
                        <TbStarFilled />
                      </span>
                      <h5 className="text-danger">{Array.isArray(ratingData) ? Math.max(ratingData.length - 1, 0) : 0} Reviews</h5>
                    </div>
                  </div>
                }
              </div>
            </div>
            <div className="dashboard-tiles">
              <div className="row">
                <div className={`col-md-6 col-sm-6 col-xs-12 ${userDetail?.logintype == "propertyUser" ? 'col-lg-3' : 'col-lg-4'}`}>
                  <div className="column">
                    <h4 className="text-black fs-6">Rooms Info</h4>
                    <div className="flex">
                      {Object.keys(roomsCount).map((key) => (
                        <div
                          className={`box ${key == "available" ? 'available' : '' ||
                            key == "blocked" ? 'blocked' : '' ||
                              key == "dirty" ? 'dirty' : '' ||
                                key == "occupied" ? 'occupied' : '' ||
                                  key == "total" ? 'total' : ''
                            }`}
                          key={key}>
                          <h5>{key}</h5>
                          <span>{roomsCount[key]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={`col-md-6 col-sm-6 col-xs-12 ${userDetail?.logintype == "propertyUser" ? 'col-lg-3' : 'col-lg-4'}`}>
                  <div className="column">
                    <h4 className="text-black fs-6">Room Occupancy</h4>
                    {finalCategoryWiseData.length > 1
                      ?
                      <Chart
                        chartType="PieChart"
                        width="100%"
                        height="300px"
                        data={hasData ? roomOccupancy : fallbackData}
                        options={
                          hasData
                            ? options
                            : {
                              ...options,
                              colors: ["#ccc"],
                              pieSliceText: "none",
                              legend: { position: "none" },
                            }
                        }
                      />
                      :
                      (
                        <DonutChart percentage={roomOccupancy?.[1]?.[1]} />
                      )}
                    {!hasData && (
                      <p style={{ textAlign: "center", marginTop: "-140px", fontWeight: "500", color: "#555" }}>No data available</p>
                    )}
                  </div>
                </div>
                <div className={`col-md-6 col-sm-6 col-xs-12 ${userDetail?.logintype == "propertyUser" ? 'col-lg-3' : 'col-lg-4'}`}>
                  <div className="column">
                    <h4 className="text-black fs-6 mb-3">Bookings By Source</h4>
                    {
                      Object.keys(bookingBySource).map((item) => {
                        return (
                          <div key={item} className="mt-2">
                            <p className="mb-1 small text-dark fw-bold">
                              {sourceLabels[item]} <span className="float-end">{bookingBySource[item]}</span>
                            </p>
                            <div className="progress" style={{ height: "15px" }}>
                              <div
                                className={`progress-bar 
                            ${item == "rroomsCount" ? "bg-warning" :
                                    item == "otaCount" ? "bg-primary" :
                                      item == "taCount" ? "bg-info" :
                                        item == "walkinCount" ? "bg-secondary" :
                                          "bg-success"
                                  }`}
                                style={{ width: `${bookingBySource[item] * 2}%` }}
                              ></div>
                            </div>
                          </div>
                        )
                      })
                    }
                    <div className="my-container">
                      {Object.keys(allBookingDetails).slice(0, 6).map((item) => {
                        return (<p key={item} className="my-tab">{item} - {allBookingDetails[item]}</p>)
                      })}
                    </div>
                  </div>
                </div>
                {
                  userDetail.logintype == "propertyUser" &&
                  <div className="col-lg-3 col-md-6 col-sm-6">
                    <div className="column">
                      <h4 className="text-black fs-6">Kitchen</h4>
                      <div style={containerStyle}>
                        {foodOrder.map((item, index) => {
                          const isLastRow = index >= foodOrder.length - 2;
                          return (
                            <div
                              key={item}
                              className={`box
          ${item === "delivered" ? "blocked" : ""}
          ${item === "inKitchen" ? "dirty" : ""}
          ${item === "ncCount" ? "nckot" : ""}
          ${item === "complementary" ? "complementary" : ""}
          ${item === "readyToServe" ? "serve" : ""}
          ${item === "totalOrders" ? "total" : ""}
          ${item === "rejected" ? "occupied" : ""}
          ${item === "newOrder" ? "available" : ""}
        `}
                              style={{ ...(isLastRow ? halfBoxStyle : boxStyle) }}
                            >
                              <h5 style={{ fontSize: "11px" }}>{foodStatusLabels[item]}</h5>
                              <span>{foodInfo[item]}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
            <div className="recent-bookings">
              <div className="flex justify-content-between align-items-center mb-3">
                <h2 className="fs-6 text-black mb-0">Upcoming Bookings</h2>
                {userDetail.logintype == "propertyUser" ?
                  <div className="d-flex align-items-center gap-1" style={{ width: "200px" }}>
                    <label htmlFor="searchText text-primary" className="mb-0">Bookings:</label>
                    <select style={{ border: "1px solid hsl(0deg 4.44% 58.8%)", borderRadius: "4px", minWidth: "125px", height: "35px" }} value={selectedBookingType} onChange={(e) => setSelectedBookingType(e.target.value)}>
                      <option value="">All</option>
                      <option value="Daily">Full Day</option>
                      <option value="Hourly">Hourly</option>
                    </select>
                  </div> : ""}
                <Link to={`/dashboard/${userDetail.logintype == "rroomUser" ? 'add-booking' : 'all-bookings'}`} style={{ display: userDetail?.role == 29 ? "none" : "" }} className=" btn-blink">View all Bookings</Link>
              </div>
              {
                bookings?.length == 0 ?
                  <TableSkeleton rows={5} columns={10} /> :
                  (
                    <div className="table-responsive">
                      <table className="front-table text-center">
                        <thead>
                          <tr>
                            {userDetail.logintype == "rroomUser" && <th>Property Name</th>}
                            <th>Guest Name</th>
                            <th>Booking Code</th>
                            <th>Source</th>
                            {userDetail.logintype == "rroomUser" && <th>OTA Booking Id</th>}
                            <th className="text-center">Arrival & Departure</th>
                            <th className="text-center">Rooms</th>
                            <th className="text-center">Nights</th>
                            <th className="text-center">Meal Plan</th>
                            <th>Room Type</th>
                            <th>Amount</th>
                            <th>Payment Status</th>
                            {/* <th>Action</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {
                            bookings?.length > 0 ?
                              bookings?.filter((data) => data?.bookingStatus == 1).map((item) => {
                                let roomCateColor = ROOM_CATEGORYES?.find((data) => {
                                  return (data?.id == item?.propertyRoomsCategoryId) || (data.name == item?.RroomCategory?.name)
                                })?.color
                                return (
                                  <tr key={item?.id}>
                                    {
                                      userDetail.logintype == "rroomUser" &&
                                      <td data-label="Property Name">{item?.PropertyMaster?.name}</td>
                                    }
                                    <td data-label="Guest Name">{item?.User ? item?.User?.name : item?.otherPersonName} {item?.adults > 1 && `+ ${item?.adults - 1}`}</td>
                                    <td className="text-uppercase fw-bold" data-label="Booking Code">
                                      <Link className="r-btn-link fs-8" to={`/dashboard/all-bookings/${item?.id}`}>
                                        {
                                          userDetail.logintype == "propertyUser" ?
                                            (item?.source?.toUpperCase() == "RROOMS" && item?.bookingCode) ||
                                            (item?.source?.toUpperCase() != "RROOMS" && item?.otaBookingId ? item?.otaBookingId : item?.bookingCode) : ''
                                        }
                                        {userDetail.logintype == "rroomUser" ? item?.bookingCode : ''}
                                      </Link>
                                    </td>
                                    <td className="text-uppercase fw-bold" data-label="Source">
                                      <span className={`${item?.source == "RRooms" ? 'text-uppercase' : ''}`}>
                                        {item.source ?? "N/A"} {item.source == "TA-" && item.referenceName}{item?.source?.toUpperCase() == "RROOMS" && item?.otaBookingId ? '-OTA' : ''}
                                      </span>
                                      {userDetail?.logintype == "rroomUser" && <p className="fs-9 m-0">{item?.referenceName}</p>}
                                    </td>
                                    {
                                      userDetail.logintype == "rroomUser" &&
                                      <td data-label="OTA Booking Id">{item?.otaBookingId}</td>
                                    }
                                    <td className="text-center" data-label="Arrival & Departure">
                                      <span><span className="text-success"><FiArrowDownRight size={18} /></span>{moment(item?.fromDate).format('DD-MM-YYYY')}</span>
                                      <span className="d-block"><span className="text-danger"><GoArrowUpRight size={18} /></span>{moment(item?.toDate).format('DD-MM-YYYY')}</span>
                                    </td>
                                    <td data-label="Rooms">
                                      <span style={{ display: 'inline-block', lineHeight: '1', padding: '0px 10px' }}>{item?.noOfRooms}</span>
                                    </td>
                                    <td data-label="Nights">
                                      <span style={{ display: 'inline-block', lineHeight: '1', padding: '0px 10px' }}>{daysDiffernce(item?.fromDate, item?.toDate)}</span>
                                    </td>
                                    <td data-label="Meal Plan">
                                      <span style={{ display: 'inline-block', lineHeight: '1', padding: '0px 10px' }}>{MealPlanText[item.breakFast]}</span>
                                    </td>
                                    <td data-label="Room Type">
                                      <span className="r-budge r-border" style={{ borderColor: roomCateColor, color: roomCateColor }}>
                                        {
                                          userDetail?.logintype == "propertyUser" ? item?.RroomCategory?.name : ROOM_CATEGORY[item?.propertyRoomsCategoryId]
                                        }
                                      </span>
                                    </td>
                                    <td data-label="Amount">₹{item?.bookingAmout}</td  >
                                    <td data-label="Payment Status">
                                      {
                                        (item.PaymentStatus == 0 && item.paymentMode == 0) || paymentModesForPayAtHotel.includes(item.paymentMode)
                                          ? 'Pay at Hotel'
                                          : item.PaymentStatus == 1 && item.paymentMode == 1
                                            ? "Prepaid"
                                            : item.PaymentStatus == 0 && item.paymentMode == 1
                                              ? "Partial Pay"
                                              : 'Unknown Payment Mode'
                                      }
                                    </td>
                                  </tr>
                                )
                              }) : ''
                          }
                        </tbody>
                      </table>
                    </div>
                  )
              }
            </div>
          </>
        ) : <SkeletonDashboard />
      }
    </>
  );
};

export default Landing;
